/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logDemoCustomContent } from 'src/core/services/telemetry/eventsNamesTemplate';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { DemoHeader } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoHeader';
import { DemoPageAgreement } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoPageAgreement';
import { DocumentInputStep } from 'src/languageStudio/appShell/prebuiltDemos/components/DocumentInputStep';
import { NextStepsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/NextStepsStep';
import { ResultsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/ResultsStep';
import { VisualizedNERResult } from 'src/languageStudio/appShell/prebuiltDemos/components/VisualizedNERResult';
import { textAnalyticsDemoSamples } from 'src/languageStudio/appShell/prebuiltDemos/utils/demoSamples';
import {
    namedEntitiesApiReferenceLink,
    namedEntitiesDocLink,
    namedEntitiesOverviewLink,
    namedEntitiesSamplesLink,
    namedEntitiesSdkLink,
    textAnalyticsPricingLink
} from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportLinks';
import {
    Language,
    namedEntitiesDemoGAModelSupportedLanguages,
    namedEntitiesDemoPreviewModelSupportedLanguages
} from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { TryoutModelVersion } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedModelVerion';
import { useTextAnalyticsCapabilities } from 'src/languageStudio/core/hooks/useTextAnalyticsCapabilities';
import { NamedEntityRecognitionDocuments } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';
import { EntityCategory, isDocumentSwaggerRtl, isDocumentSwaggerWordTokenized } from 'src/languageStudio/core/utils/demosUtils';

const messages = defineMessages({
    pageHeader: { id: 'languageStudio.namedEntityDemoPage.pageHeader', defaultMessage: 'Extract named entities' },
    hideCardsMessage: { id: 'languageStudio.namedEntityDemoPage.hideCardsMessage', defaultMessage: 'Turn on cards to see named entities' },
    capabilityDescription: {
        id: 'languageStudio.namedEntityDemoPage.capabilityDescription',
        defaultMessage: `This prebuilt capability uses Named Entity Recognition (NER) to
        identify entities in text and categorize them into pre-defined classes or types such as:
        person, location, event, product, and organization.`
    },
    emptyState: {
        id: 'languageStudio.namedEntityDemoPage.emptyState',
        defaultMessage: `Your named entities results will appear here once you enter or upload
        some text and press Run. The file format needs to be .txt`
    },
    signedOutEmptyState: {
        id: 'languageStudio.namedEntityDemoPage.signedOutEmptyState',
        defaultMessage: `Your named entities results will appear here once you press Run.`
    },
    namedEntitiesIdentifiedTitle: {
        id: 'languageStudio.namedEntityDemoPage.namedEntitiesIdentifiedTitle',
        defaultMessage: 'Named entities identified'
    }
});

/**
 * Displays the Named Entity demo page.
 */
export const NamedEntityDemoPage = observer(() => {
    const { rootStore } = useGlobalState();
    const { localize } = useLocalization();
    const { getNamedEntities, getCurlCommand, namedEntities, tracker, resetResults, detectedLanguages } = useTextAnalyticsCapabilities();

    const { isSignedIn } = rootStore.authStore;

    const [document, setDocument] = React.useState('');
    const [language, setLanguage] = React.useState<Language['code']>('en');
    const [modelVersion, setModelVersion] = React.useState<TryoutModelVersion>('GA');
    const [languageAutoDetection, setLanguageAutoDetection] = React.useState(false);
    const [selectedSampleTitle, setSelectedSampleTitle] = React.useState<string>(null);

    const languageList =
        modelVersion === 'GA' ? namedEntitiesDemoGAModelSupportedLanguages : namedEntitiesDemoPreviewModelSupportedLanguages;

    const handleDocumentChange = (newDocument: string) => {
        if (namedEntities) {
            resetResults();
        }
        setDocument(newDocument);
    };

    const handleRunDemo = () => {
        if (!selectedSampleTitle) {
            logDemoCustomContent('Entity Extraction');
        }

        getNamedEntities({
            document,
            language,
            modelVersion,
            sampleTitle: selectedSampleTitle,
            autoDetectLanguage: languageAutoDetection
        });
    };

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <DemoHeader
                featureName="Entity Extraction"
                supportedPlatforms={['cloud']}
                getSdkLink={namedEntitiesSdkLink}
                documentationLink={namedEntitiesDocLink}
                learnMoreLink={namedEntitiesOverviewLink}
                demoTitle={localize(messages.pageHeader)}
                gitHubSamplesLink={namedEntitiesSamplesLink}
                demoDescription={localize(messages.capabilityDescription)}
                demoVersion={modelVersion === 'GA' ? LuisConstants.Demos.ApiVersions.GA : LuisConstants.Demos.ApiVersions.Preview}
            />

            <DocumentInputStep
                document={document}
                selectedLanguage={language}
                onLanguageChange={setLanguage}
                featureName="Entity Extraction"
                selectedModelVersion={modelVersion}
                acceptedResourceType="TextAnalytics"
                disableModelVersionSelection={!FeatureToggles.NerTryoutPreviewModel}
                samplesList={textAnalyticsDemoSamples}
                onModelVersionChange={setModelVersion}
                onDocumentChange={handleDocumentChange}
                selectedSampleTitle={selectedSampleTitle}
                languagesList={languageList}
                supportedRegionLink={namedEntitiesApiReferenceLink}
                onLanguageAutoDetectionClick={setLanguageAutoDetection}
                onSampleClick={sample => setSelectedSampleTitle(sample?.title)}
                isTextRTL={isDocumentSwaggerRtl(detectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language)}>
                <DemoPageAgreement
                    featureName="Entity Extraction"
                    onRunDemoClick={handleRunDemo}
                    isRunDisabled={!document.length}
                    acceptedResourceType="TextAnalytics"
                    pricingLink={textAnalyticsPricingLink}
                />
            </DocumentInputStep>

            <ResultsStep<NamedEntityRecognitionDocuments>
                results={namedEntities}
                isLoading={tracker.isRunning}
                featureName="Entity Extraction"
                emptyStateMessage={localize(isSignedIn ? messages.emptyState : messages.signedOutEmptyState)}>
                <VisualizedNERResult<EntityCategory>
                    document={document}
                    featureName="Entity Extraction"
                    detectedLanguage={detectedLanguages}
                    entities={namedEntities?.documents[0]?.entities || []}
                    hiddenEntitiesMessage={localize(messages.hideCardsMessage)}
                    entitiesSectionTitle={localize(messages.namedEntitiesIdentifiedTitle)}
                    isLabelerRTL={isDocumentSwaggerRtl(detectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language)}
                    isLabelerWordTokenized={isDocumentSwaggerWordTokenized(
                        detectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language
                    )}
                />
            </ResultsStep>

            <NextStepsStep
                sdkLink={namedEntitiesSdkLink}
                isResultsEmpty={!namedEntities}
                featureName="Entity Extraction"
                documentationLink={namedEntitiesDocLink}
                sampleCodeLink={namedEntitiesSamplesLink}
                demosToDisplay={['pii', 'keyPhrases', 'taForHealth']}
                curlCommand={getCurlCommand({
                    endpoint: 'EntityRecognition',
                    text: document,
                    apiVersion: modelVersion === 'GA' ? LuisConstants.Demos.ApiVersions.GA : LuisConstants.Demos.ApiVersions.Preview,
                    languageAutoDetection,
                    language
                })}
            />
        </Stack>
    );
});
